<template>
	<div>
        <ActionModal 
            v-if="!loaded"
            text="loading-report"
            class="my-auto"
        />
		<div class="row" v-else>
			<div class="col-5 mb-4">
				<h1>{{ $t("curriculum-mapping-report") }}</h1>
			</div>
			<div class="col-7 mb-4">
				<button
                    class="btn btn-outline-secondary ml-2 float-lg-right float-left"
                    :title="$t('download')"
                    @click="getCSV()"
                    :disabled="report_mode === 'outcomes' ? search.lo_groups.length == 0 : search.curricula.length == 0"
                >
                    <b-icon-download />
                </button>
			</div>
		</div>
        <div class="row mb-3">
            <div class="col-12">
                <div class="btn-group" role="group">
                    <button
                        type="button"
                        class="btn"
                        :class="report_mode === 'outcomes' ? 'btn-primary' : 'btn-outline-primary'"
                        @click="report_mode = 'outcomes'"
                    >
                        {{ $t('report-by-learning-outcome') }}
                    </button>
                    <button
                        type="button"
                        class="btn"
                        :class="report_mode === 'curricula' ? 'btn-primary' : 'btn-outline-primary'"
                        @click="report_mode = 'curricula'"
                    >
                        {{ $t('report-by-curriculum') }}
                    </button>
                </div>
            </div>
        </div>
        <div class="row align-items-end" v-if="report_mode === 'outcomes'">
            <div class="col-6">
                <div class="form-group mb-0">
					<label class="d-block">{{ $t("learning-outcome-groups") }}</label>
                    <div
                        class="pseudo-input"
                    >
                        <input
                            type="search"
                            class="input-bare w-full flex-1"
                            :placeholder="$t('learning-outcome-groups')"
                            v-if="!search.lo_groups?.length"
                            @click="selector('lo_groups')"
                            @keyup.enter="selector('lo_groups')"
                        />
                        <span
                            class="badge badge-secondary badge-sm mr-2 mb-2"
                            v-for="(log, index) in search.lo_groups"
                            v-bind:key="index"
                        >
                            {{ log.title }}
                            <a @click="removeItem(log)"><b-icon icon="x"></b-icon></a>
                        </span>
                        <span
                            class="badge badge-primary badge-sm mr-2 mb-2"
                            v-if="search.lo_groups?.length"
                            @click="selector('lo_groups')"
                            @keyup.enter="selector('lo_groups')"
                            :aria-label="$t('learning-outcome-group') + ' ' + $t('add')"
                        >
                            <b-icon icon="plus"></b-icon> {{ $t("add") }}
                        </span>
                        <span
                            class="badge badge-danger badge-sm mr-2 mb-2"
                            v-if="search.lo_groups?.length"
                            @click="search.lo_groups=[]"
                            @keyup.enter="search.lo_groups=[]"
                            tabindex="0"
                            :aria-label="$t('learning-outcome-group') + ' ' + $t('clear-all')"
                        >
                            <b-icon icon="x"></b-icon> {{ $t('clear-all') }}
                        </span>
                    </div>
                </div>
            </div>
            <div class="col-6 text-right">
                <button
                    class="btn btn-primary"
                    @click="getReport"
                    :disabled="search.lo_groups.length == 0"
                >
                    {{ $t('generate') }}
                </button>
            </div>
        </div>
        <div class="row align-items-end" v-else>
            <div class="col-6">
                <div class="form-group mb-0">
					<label class="d-block">{{ $t("curricula") }}</label>
                    <div
                        class="pseudo-input"
                    >
                        <input
                            type="search"
                            class="input-bare w-full flex-1"
                            :placeholder="$t('select-curricula')"
                            v-if="!search.curricula?.length"
                            @click="selector('curricula')"
                            @keyup.enter="selector('curricula')"
                        />
                        <span
                            class="badge badge-secondary badge-sm mr-2 mb-2"
                            v-for="(curriculum, index) in search.curricula"
                            v-bind:key="index"
                        >
                            {{ curriculum.title }}
                            <a @click="removeItem(curriculum, 'curricula')"><b-icon icon="x"></b-icon></a>
                        </span>
                        <span
                            class="badge badge-primary badge-sm mr-2 mb-2"
                            v-if="search.curricula?.length"
                            @click="selector('curricula')"
                            @keyup.enter="selector('curricula')"
                            :aria-label="$t('curricula') + ' ' + $t('add')"
                        >
                            <b-icon icon="plus"></b-icon> {{ $t("add") }}
                        </span>
                        <span
                            class="badge badge-danger badge-sm mr-2 mb-2"
                            v-if="search.curricula?.length"
                            @click="search.curricula=[]"
                            @keyup.enter="search.curricula=[]"
                            tabindex="0"
                            :aria-label="$t('curricula') + ' ' + $t('clear-all')"
                        >
                            <b-icon icon="x"></b-icon> {{ $t('clear-all') }}
                        </span>
                    </div>
                </div>
            </div>
            <div class="col-6 text-right">
                <button
                    class="btn btn-primary"
                    @click="getNodeReport"
                    :disabled="search.curricula.length == 0"
                >
                    {{ $t('generate') }}
                </button>
            </div>
        </div>

        <div class="list-group border-radius-2 no-border mt-3" v-if="report_mode === 'outcomes' && selected_outcome_groups.length>0">
            <div class="list-group-item">
                <div class="row mb-1" v-for="(outcome_group,index) in selected_outcome_groups" :key="index">
                    <div class="col-12">
                        <div class="card">
                            <h2
                                class="card-header d-flex justify-content-between align-items-center"
                                style="cursor: pointer;"
                                @click="toggleExpand(expanded_groups, outcome_group.id)"
                            >
                                <span>
                                    <b-icon :icon="expanded_groups[outcome_group.id] ? 'chevron-down' : 'chevron-right'"></b-icon>
                                    {{ outcome_group.title }}
                                </span>
                                <b-badge pill variant="secondary">{{ Object.keys(outcome_group.learningoutcomes).length }}</b-badge>
                            </h2>
                            <div class="card-body" v-if="expanded_groups[outcome_group.id]">
                                <p v-if="outcome_group.note">
                                    <strong>{{  $t('notes') }}: </strong>{{ outcome_group.note }}
                                </p>
                                <div class="row">
                                    <b-list-group>
                                        <b-list-group-item v-for="LO in outcome_group.learningoutcomes" :key="LO.id">
                                            <b-badge variant="info">{{LO.code}}</b-badge> {{ LO.title }}
                                            <b-list-group>
                                                <b-list-group-item v-for="node in LO.curriculummap" :key="node.id">
                                                    {{ node.path }}
                                                </b-list-group-item>
                                            </b-list-group>
                                            <b-list-group v-if="LO.linked_outcomes.length > 0">
                                                <b-list-group-item v-for="link in LO.linked_outcomes" :key="link.id">
                                                    <b-icon icon="check" size="22"></b-icon><b-badge variant="info">{{link.code}}</b-badge> {{ link.title }} <b-badge pill variant="primary">{{ getOutcomeGroupTitle(link.learningoutcomegroup_id) }}</b-badge>
                                                </b-list-group-item>
                                            </b-list-group>
                                        </b-list-group-item>
                                    </b-list-group>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!--table v-else class="table">
                        <tr v-for="item in Array(5)" v-bind:key="item">
                            <td>
                                <div class="skeleton midnight h16 w-half"></div>
                            </td>
                        </tr>
                    </table-->
                </div>
            </div>
		</div>

        <div class="list-group border-radius-2 no-border mt-3" v-if="report_mode === 'curricula' && selected_curricula_nodes.length>0">
            <div class="list-group-item">
                <div class="row mb-1" v-for="(curriculum,index) in selected_curricula_nodes" :key="index">
                    <div class="col-12">
                        <div class="card">
                            <h2
                                class="card-header d-flex justify-content-between align-items-center"
                                style="cursor: pointer;"
                                @click="toggleExpand(expanded_curricula, curriculum.id)"
                            >
                                <span>
                                    <b-icon :icon="expanded_curricula[curriculum.id] ? 'chevron-down' : 'chevron-right'"></b-icon>
                                    {{ curriculum.title }}
                                </span>
                                <b-badge pill variant="secondary">{{ curriculum.nodes.length }}</b-badge>
                            </h2>
                            <div class="card-body" v-if="expanded_curricula[curriculum.id]">
                                <div class="row">
                                    <b-list-group>
                                        <b-list-group-item v-for="node in curriculum.nodes" :key="node.id">
                                            {{ node.path }}
                                            <b-list-group v-if="node.learningoutcomes.original.length > 0">
                                                <b-list-group-item v-for="LO in node.learningoutcomes.original" :key="LO.id">
                                                    <b-badge variant="info">{{LO.code}}</b-badge> {{ LO.title }}
                                                </b-list-group-item>
                                            </b-list-group>
                                            <b-list-group v-if="node.learningoutcomes.linked.length > 0">
                                                <b-list-group-item v-for="link in node.learningoutcomes.linked" :key="link.id">
                                                    <b-icon icon="check" size="22"></b-icon><b-badge variant="info">{{link.code}}</b-badge> {{ link.title }} <b-badge pill variant="primary">{{ getOutcomeGroupTitle(link.learningoutcomegroup_id) }}</b-badge>
                                                </b-list-group-item>
                                            </b-list-group>
                                            <p
                                                class="text-muted mb-0 mt-1"
                                                v-if="node.learningoutcomes.original.length == 0 && node.learningoutcomes.linked.length == 0"
                                            >
                                                {{ $t('no-learning-outcomes-linked') }}
                                            </p>
                                        </b-list-group-item>
                                    </b-list-group>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
		</div>

	</div>
</template>

<script>
import Papa from 'papaparse';
import ActionModal from '../../../components/ActionModal.vue'
import CurriculumMap from 'src/modules/curriculummap.js'

export default {
    components: {
        ActionModal
    },
	data: function () {
		return {
            outcome_groups: [],
            showDeleteButton: {},
            loaded: false,
            report_mode: 'outcomes',
            selected_outcome_groups:[],
            expanded_groups:{},
            selected_curricula_nodes:[],
            expanded_curricula:{},
            selected_nodes: [],
            loaded_group:{},
            curricula:[],
            search: {
                lo_groups: [],
                components: [],
                curricula: []
            },
            components: []
		};
	},
	mounted() {
        this.getOutcomesGroups();

        this.curriculummap = new CurriculumMap(this.$route.params.id);
        this.curriculummap.getNodes({ just_roots: true }).then((res) => {
            this.curricula = res
        })
        .finally(() => this.loaded = true)
	},
	methods: {
		getOutcomesGroups() {
            this.$cfetch(`${this.$settings.APIBaseURL}/learningoutcome/group/list`, {
                    method: 'post'
            }).then((res) => {
                if (res.status === "SUCCESS") {
                    this.outcome_groups = res.results;
                    this.outcome_groups_titles = this.outcome_groups.map(x => {return x.title;})
                }
                
            });
		},
        getComponents() {
            this.$cfetch(`${this.$settings.APIBaseURL}/curriculummap/component/list`, {
                    method: 'post'
            }).then((res) => {
                if (res.status === "SUCCESS") {
                    this.components = res.results;
                }
                
            });
		},
        getOutcomeGroupTitle(log_id){
            const group = this.outcome_groups.find(x => x.id == log_id);
            return group ? group.title : 'Unknown Group';
        },
        toggleOutcomeGrp(outcome_group) {
            const index = this.selected_outcome_groups.findIndex(item => item.id == outcome_group.id);
            if (index > -1) {
                this.selected_outcome_groups.splice(index, 1);
                this.loaded_group[outcome_group.id] = false;
            } else {
                this.getReport(outcome_group.id)
            }
        },
        getReport(){
            this.loaded = false;
            this.selected_outcome_groups = [];
            this.expanded_groups = {};
            this.$cfetch(`${this.$settings.APIBaseURL}/curriculummap/getreport`, {
                    method: 'post',
                    data: this.search
            })
            .then((res) => {
                if (res.status === "SUCCESS") {
                    this.selected_outcome_groups = res.result;
                }

            })
            .finally(() => this.loaded = true);
        },
        getNodeReport(){
            this.loaded = false;
            this.selected_curricula_nodes = [];
            this.expanded_curricula = {};
            this.$cfetch(`${this.$settings.APIBaseURL}/curriculummap/getnodereport`, {
                    method: 'post',
                    data: { curricula: this.search.curricula }
            })
            .then((res) => {
                if (res.status === "SUCCESS") {
                    this.selected_curricula_nodes = res.result;
                }

            })
            .finally(() => this.loaded = true);
        },
        toggleExpand(map, id){
            this.$set(map, id, !map[id]);
        },
        createReport(){
            const user_selection = this.selected_outcome_groups.flatMap(x =>{
                let rows = [];

                // Set headers
                const learning_outcomes_group = {
                    'Learning Outcome Group' : x.title,
                    //'Learning Outcome Group Notes': x.note,
                    'Learning Outcome' : null,
                    //'Learning Outcome Note' : null,
                    'Path': null
                };
                rows.push(learning_outcomes_group);
                // We loop through each learning outcome
                Object.keys(x.learningoutcomes).forEach(key => {
                    const lo = x.learningoutcomes[key]
                    // Some formatting
                    const code = lo.code ? `${lo.code}:` : '';
                    const learningoutcome = `${code} ${lo.title}`;

                    // Generate a row, notice LO Group is null
                    const learning_outcomes = {
                        'Learning Outcome Group' : null,
                        //'Learning Outcome Group Notes': null,
                        'Learning Outcome' : learningoutcome,
                        //'Learning Outcome Note' : y.note,
                        'Path': null
                    }

                    rows.push(learning_outcomes);

                    // Finally, we loop over nodes
                    //lo.curriculummap.forEach( z => {
                    Object.keys(lo.curriculummap).forEach( key => {
                        const entry = lo.curriculummap[key]
                        const node = {
                            'Learning Outcome Group' : null,
                            //'Learning Outcome Group Notes': null,
                            'Learning Outcome' : null,
                            //'Learning Outcome Note' : null,
                            'Path': entry.path
                        }

                        rows.push(node);
                    })

                    if(lo.linked_outcomes){
                        Object.keys(lo.linked_outcomes).forEach( key => {
                            const entry = lo.linked_outcomes[key]
                            const node = {
                                'Learning Outcome Group' : null,
                                //'Learning Outcome Group Notes': null,
                                'Learning Outcome' : null,
                                //'Learning Outcome Note' : null,
                                'Path': `(Learning Outcome) [${this.getOutcomeGroupTitle(entry.learningoutcomegroup_id)}] ${entry.code}: ${entry.title}`
                            }

                            rows.push(node);
                        })
                    }
                });

                return rows;
            });
            return user_selection;
        },
        createNodeReport(){
            const user_selection = this.selected_curricula_nodes.flatMap(x => {
                let rows = [];

                // Set headers
                const curriculum_row = {
                    'Curriculum': x.title,
                    'Node': null,
                    'Learning Outcome': null
                };
                rows.push(curriculum_row);

                // We loop through each node
                x.nodes.forEach(node => {
                    rows.push({
                        'Curriculum': null,
                        'Node': node.path,
                        'Learning Outcome': null
                    });

                    // Directly tethered learning outcomes
                    node.learningoutcomes.original.forEach(lo => {
                        const code = lo.code ? `${lo.code}:` : '';
                        rows.push({
                            'Curriculum': null,
                            'Node': null,
                            'Learning Outcome': `${code} ${lo.title}`
                        });
                    });

                    // Learning outcomes linked in via another outcome tethered to this node
                    node.learningoutcomes.linked.forEach(link => {
                        rows.push({
                            'Curriculum': null,
                            'Node': null,
                            'Learning Outcome': `(Learning Outcome) [${this.getOutcomeGroupTitle(link.learningoutcomegroup_id)}] ${link.code}: ${link.title}`
                        });
                    });
                });

                return rows;
            });
            return user_selection;
        },
        getCSV(){
            let report = this.report_mode === 'outcomes' ? this.createReport() : this.createNodeReport();
            // Get the CSV via a Promise
            return new Promise((resolve, reject) => {
                try {
                    const csv = Papa.unparse(report);
                    resolve(csv);
                } catch (error) {
                    console.error('Error unparsing CSV file:', error);
                    reject(error);
                }
            }).then(res => {
                // Create a Blob from the CSV string
                const blob = new Blob([res], { type: 'text/csv' });

                // Give it a download link
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = this.report_mode === 'outcomes' ? `learning_outcomes_report.csv` : `curriculum_nodes_report.csv`;

                // Trigger download
                a.click();

                // Housekeeping
                URL.revokeObjectURL(url);
            });
        },
        selector(type) {
            const dict = {
                lo_groups : {
                    title: this.$t("select-learning-outcome-groups"),
                    data: this.outcome_groups
                },
                components : {
                    title: this.$t("select-components"),
                    data: this.components
                },
                curricula : {
                    title: this.$t("select-curricula"),
                    data: this.curricula
                }
            }
			const self = this;
            this.$store.commit("openModal", {
                type: "selector",
                title: dict[type].title,
                selectCallback: function (selection) {
                    const isSelected = self.search[type].find((x, i) => {
                        if (x.id === selection.id) {
                            self.$delete(self.search[type], i);
                            return true;
                        }
                    });
                    if (!isSelected) {
                        self.search[type].push(selection);
                    }
                },
                selected: this.search[type],
                data: dict[type].data,
                captionKey: "title",
                selectAllBtn: true
            });
		},
        removeItem(item, type = 'lo_groups'){
            this.search[type] = this.search[type].filter(x => x.id != item.id)
        }
	},
    computed:{
       selectedOutcomeGroups(){
            if(this.selected_outcome_groups.length>0) {
                return this.outcome_groups.filter(x =>  this.selected_outcome_groups.includes(x.title));
            }
            return [];
        },
        // allowDownload() {
            
        // }
    }
};
</script>
